import CancelIcon from "@mui/icons-material/Cancel";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import {
  Card,
  CardContent,
  CardHeader,
  CardTypeMap,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import { styled } from "@mui/system";
import React, { useContext, useEffect, useState } from "react";

import { SessionContext } from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { Appointment, EnhancedAppointment } from "../../Models/Appointment";
import { ServiceGroup } from "../../Models/ServiceGroup";
import { DateTime } from "../../Types/DateTime";
import { Error } from "../../Utils/Errors";
import { useApi } from "../../Utils/useApi";

interface Props {
  /** The id if the current appointment being rescheduled, if any. */
  reschedulingApptId?: string;
  /** The list of services into which we are allowing the user to book appointments. */
  enabledServices: ServiceGroup[];
  /** The callback to be executed when a usr toggles an appointment for rescheduling. */
  setRescheduleAppointment: (appt?: EnhancedAppointment) => void;
}

const BookingWizardPreviousAppts: React.FC<Props> = ({
  enabledServices,
  reschedulingApptId,
  setRescheduleAppointment
}) => {
  const {
    lead: { id: leadId },
    customer: { id: customerId, centerId }
  } = useContext(SessionContext);
  const { centers } = useContext(UserContext);
  const [appointments, setAppointments] = useState<EnhancedAppointment[]>();

  // GET CUSTOMER APPOINTMENTS
  const { response: getAppointments } = useApi<Appointment[]>({
    url: `Legacy/V0/Contacts/${leadId}/Customers/${customerId}/Appointments`,
    valid: Boolean(customerId),
    error: Error.GetCustomerAppointments
  });

  useEffect(() => {
    if (!getAppointments) return;
    if (!getAppointments.length) return setAppointments([]);
    const center = centers.find(c => c.id === getAppointments[0].centerId);
    if (center) setAppointments(getAppointments.map(a => new EnhancedAppointment(a, center.luxonTimeZone)));
  }, [getAppointments, centers]);

  return (
    <Grid container direction="column" rowSpacing={4} wrap="nowrap" sx={{ m: 0, height: "100%" }}>
      <Grid item>
        <Typography align="center" variant="body1">
          <strong>{`Booked Appointments${appointments ? ` (${appointments.length})` : ""}:`}</strong>
        </Typography>
      </Grid>
      {!appointments ? (
        <Grid item xs container justifyContent="center" alignItems="center">
          <CircularProgress size="4rem" />
        </Grid>
      ) : appointments.length ? (
        <Grid item xs sx={{ width: "100%", mt: -4, overflowY: "auto", overflowX: "hidden", pr: 2 }}>
          <Grid container direction="column" rowSpacing={2} wrap="nowrap" sx={{ m: 0, height: "100%" }}>
            {appointments.map((appointment, index) => {
              const reschedulingThisAppt = reschedulingApptId === appointment.id;
              const allowReschedule =
                // allow rescheduling if no appointment is being rescheduled, or this is the appointment being rescheduled
                (!reschedulingApptId || reschedulingThisAppt) &&
                // and the appointment is not closed
                !appointment.isClosed &&
                // and the appointment center is the same as the current center
                appointment.centerId === centerId &&
                // and it has at least one enabled service
                appointment.services.some(apptService => enabledServices.some(s => s.service.id === apptService.id));
              return (
                <Grid item key={index}>
                  <BookingWizardPreviousAppt
                    appointment={appointment}
                    isRescheduling={reschedulingThisAppt}
                    onReschedule={
                      allowReschedule
                        ? () => setRescheduleAppointment(reschedulingThisAppt ? undefined : appointment)
                        : undefined
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      ) : (
        <Grid item>
          <Typography variant="body2" align="center">
            None
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

interface BookingWizardPreviousApptProps {
  /** The appointment that this card is representing. */
  appointment: EnhancedAppointment;
  /** The flag indicating if the appoinment for this card is currently being rescheduled. */
  isRescheduling: boolean;
  /** The handler to be executed when the reschedule button is clicked. If not provided, the button is not shown.  */
  onReschedule?: () => void;
}

const BookingWizardPreviousAppt: React.FC<BookingWizardPreviousApptProps> = ({
  appointment,
  isRescheduling,
  onReschedule
}) => {
  return (
    <AppointmentCard color={isRescheduling ? "primary" : "default"}>
      <CardHeader
        action={
          onReschedule && (
            <Tooltip title={isRescheduling ? "Cancel Reschedule" : "Reschedule"}>
              <IconButton onClick={onReschedule}>{isRescheduling ? <CancelIcon /> : <EventRepeatIcon />}</IconButton>
            </Tooltip>
          )
        }
        title={appointment.services.map(s => s.name).join(", ")}
        subheader={`${appointment.startTime.toLocaleString(DateTime.DATE_MED)} | ${appointment.startTime.toLocaleString(
          DateTime.TIME_SIMPLE
        )} - ${appointment.endTime.toLocaleString(DateTime.TIME_SIMPLE)}`}
        titleTypographyProps={{ variant: "h6" }}
      />
      <CardContent>
        <Typography variant="body2">{appointment.note}</Typography>
      </CardContent>
    </AppointmentCard>
  );
};

interface AppointmentCardProps extends Partial<CardTypeMap> {
  color: "primary" | "default";
}
const AppointmentCard = styled(Card)<AppointmentCardProps>(({ theme, color = "default" }) => {
  if (color === "primary")
    return {
      "& .MuiCardHeader-root": {
        backgroundColor: theme.palette.primary.light,
        padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`
      }
    };
  return {
    "& .MuiCardHeader-root": {
      backgroundColor: theme.palette.action.disabled,
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`
    }
  };
});

export default BookingWizardPreviousAppts;
